import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useState, useEffect } from "react";
import {
    Container,
    Card,
    Form,
    Button,
    Row,
    Col,
    Table,
    Spinner,
    InputGroup,
    Badge,
    Alert
} from "react-bootstrap";
import {
    FaSearch,
    FaSave,
    FaFileAlt,
    FaUserTie,
    FaExclamationTriangle,
    FaInfoCircle,
    FaUsers,
    FaCog,
    FaFileSignature
} from "react-icons/fa";
import {
    getFirestore,
    collection,
    getDocs,
    doc,
    getDoc,
    setDoc,
    deleteDoc,
    serverTimestamp
} from "firebase/firestore";
import { Link } from "react-router-dom";
import firebaseApp from "../../firebase/credenciales";
import Swal from "sweetalert2";
import { logBusinessAction } from "../../services/AuditService";

const db = getFirestore(firebaseApp);

function ConfiguracionDocumentos({ usuario }) {
    // Estados principales
    const [documentosBase, setDocumentosBase] = useState([]);
    const [documentosUsuario, setDocumentosUsuario] = useState([]);
    const [dniUsuario, setDniUsuario] = useState("");
    const [usuarioInfo, setUsuarioInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [busquedaRealizada, setBusquedaRealizada] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [listaMaestraNoConfigurada, setListaMaestraNoConfigurada] = useState(false);

    // Obtener la lista base de documentos al cargar el componente
    useEffect(() => {
        const obtenerDocumentosBase = async () => {
            try {
                setLoading(true);
                const rucEmpresa = usuario.ruc || "20538856674"; // RUC por defecto si no existe

                // Leer documentos de la lista maestra
                const documentosRef = collection(db, `onboarding_lista/${rucEmpresa}/documentos`);
                const documentosSnap = await getDocs(documentosRef);

                const documentos = [];
                documentosSnap.forEach((doc) => {
                    documentos.push({
                        id: doc.id,
                        ...doc.data()
                    });
                });

                // Si no hay documentos, mostrar mensaje
                if (documentos.length === 0) {
                    setListaMaestraNoConfigurada(true);
                    setDocumentosBase([]);
                } else {
                    // Ordenar documentos por ID (asumiendo que son numéricos)
                    documentos.sort((a, b) => parseInt(a.id) - parseInt(b.id));
                    setDocumentosBase(documentos);
                    setListaMaestraNoConfigurada(false);
                }
            } catch (error) {
                console.error("Error al obtener documentos base:", error);
                setError("Error al cargar la lista de documentos base");
                setListaMaestraNoConfigurada(true);
            } finally {
                setLoading(false);
            }
        };

        obtenerDocumentosBase();
    }, [usuario.ruc]);

    // Buscar usuario por DNI
    const buscarUsuario = async () => {
        if (listaMaestraNoConfigurada) {
            Swal.fire({
                title: "Lista maestra no configurada",
                text: "Primero debes configurar la lista maestra de documentos antes de asignarlos a usuarios",
                icon: "warning"
            });
            return;
        }

        if (!dniUsuario || dniUsuario.length < 8) {
            setError("Ingrese un DNI válido (mínimo 8 dígitos)");
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Buscar información del usuario
            const userRef = doc(db, "users", dniUsuario);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
                setUsuarioInfo(userSnap.data());
            } else {
                // Si no existe, crear un objeto temporal
                setUsuarioInfo({
                    name: "Usuario",
                    apellidos: "DNI: " + dniUsuario,
                    email: `${dniUsuario}@temp.com`,
                    dni: dniUsuario
                });
            }

            // Buscar documentos asignados al usuario
            const documentosRef = collection(db, `onboarding/${dniUsuario}/documentos`);
            const documentosSnap = await getDocs(documentosRef);

            let documentosAsignados = [];
            documentosSnap.forEach((doc) => {
                documentosAsignados.push({
                    id: doc.id,
                    ...doc.data()
                });
            });

            // Si no hay documentos asignados, iniciar con la lista base pero todos desmarcados
            if (documentosAsignados.length === 0) {
                documentosAsignados = documentosBase.map(doc => ({
                    ...doc,
                    selected: false,
                    title: doc.title,
                    obligatorio: doc.obligatorio
                }));
            } else {
                // Si hay documentos asignados, marcar los que están en la lista base
                documentosAsignados = documentosBase.map(baseDoc => {
                    const docAsignado = documentosAsignados.find(d => d.id === baseDoc.id);
                    return {
                        ...baseDoc,
                        selected: !!docAsignado,
                        obligatorio: docAsignado ? docAsignado.obligatorio : baseDoc.obligatorio
                    };
                });
            }

            setDocumentosUsuario(documentosAsignados);
            setBusquedaRealizada(true);
        } catch (error) {
            console.error("Error al buscar usuario:", error);
            setError("Error al buscar información del usuario o sus documentos");
        } finally {
            setLoading(false);
        }
    };

    // Manejar cambio en checkbox
    const handleDocumentoChange = (id, checked) => {
        setDocumentosUsuario(prevDocs =>
            prevDocs.map(doc =>
                doc.id === id ? { ...doc, selected: checked } : doc
            )
        );
    };

    // Manejar cambio en checkbox de obligatorio
    const handleObligatorioChange = (id, checked) => {
        setDocumentosUsuario(prevDocs =>
            prevDocs.map(doc =>
                doc.id === id ? { ...doc, obligatorio: checked } : doc
            )
        );
    };

    // Guardar configuración individual
    const guardarConfiguracion = async () => {
        if (!dniUsuario || !usuarioInfo) {
            setError("No hay un usuario seleccionado");
            return;
        }

        try {
            setGuardando(true);

            // Eliminar documentos existentes primero
            const documentosRef = collection(db, `onboarding/${dniUsuario}/documentos`);
            const documentosExistentes = await getDocs(documentosRef);

            const eliminaciones = [];
            documentosExistentes.forEach(documento => {
                eliminaciones.push(deleteDoc(documento.ref));
            });

            await Promise.all(eliminaciones);

            // Guardar solo los documentos seleccionados
            const documentosSeleccionados = documentosUsuario.filter(doc => doc.selected);

            const guardados = [];
            for (const documento of documentosSeleccionados) {
                const docRef = doc(db, `onboarding/${dniUsuario}/documentos`, documento.id);
                guardados.push(setDoc(docRef, {
                    id: documento.id,
                    title: documento.title,
                    obligatorio: documento.obligatorio,
                    activo: true,
                    completed: false,
                    type: documento.type || "upload",
                    asignadoPor: usuario.email || usuario.nombre,
                    asignadoEl: serverTimestamp()
                }));
            }

            await Promise.all(guardados);

            // AUDITORÍA
            try {
                await logBusinessAction("CONFIGURACION_DOCUMENTOS_ONBOARDING", "GestionOnboarding", {
                    dniEditado: dniUsuario,
                    cambiosRealizados: {
                        configuracionIndividual: {
                            anterior: `${documentosExistentes.size} documentos`,
                            nuevo: `${documentosSeleccionados.length} documentos`
                        }
                    },
                    documentosSeleccionados: documentosSeleccionados.map(d => d.title)
                }, usuario);
            } catch (e) {
                console.warn("No se pudo registrar la auditoría", e);
            }

            Swal.fire({
                title: "¡Configuración guardada!",
                text: `Se asignaron ${documentosSeleccionados.length} documentos para el usuario con DNI ${dniUsuario}`,
                icon: "success"
            });
        } catch (error) {
            console.error("Error al guardar configuración:", error);
            Swal.fire({
                title: "Error",
                text: "Hubo un problema al guardar la configuración",
                icon: "error"
            });
        } finally {
            setGuardando(false);
        }
    };

    // ASIGNACIÓN MASIVA PARA TODOS LOS USUARIOS DEL RUC
    const asignarDocumentosATodos = async () => {
        if (listaMaestraNoConfigurada) {
            Swal.fire({
                title: "Lista maestra no configurada",
                text: "Primero debes configurar la lista maestra de documentos antes de asignarlos a usuarios",
                icon: "warning"
            });
            return;
        }

        if (!usuario.ruc) {
            Swal.fire({
                title: "Error",
                text: "No se encontró el RUC de la empresa.",
                icon: "error"
            });
            return;
        }

        try {
            setGuardando(true);

            // 1. Obtén todos los usuarios con ese RUC
            const usersRef = collection(db, "users");
            const usersSnap = await getDocs(usersRef);
            const usuariosEmpresa = [];
            usersSnap.forEach(docu => {
                const data = docu.data();
                if (data.rucEmpresa === usuario.ruc) {
                    usuariosEmpresa.push({ dni: data.dni, email: data.email, nombre: data.name });
                }
            });

            if (usuariosEmpresa.length === 0) {
                Swal.fire({
                    title: "Sin usuarios",
                    text: "No se encontraron usuarios con el RUC de la empresa.",
                    icon: "warning"
                });
                return;
            }

            // 2. Prepara los documentos seleccionados
            const documentosSeleccionados = documentosUsuario.filter(doc => doc.selected);

            if (documentosSeleccionados.length === 0) {
                Swal.fire({
                    title: "Sin documentos",
                    text: "No hay documentos seleccionados para asignar.",
                    icon: "warning"
                });
                return;
            }

            // 3. Asigna los documentos a cada usuario
            let totalAsignados = 0;
            for (const user of usuariosEmpresa) {
                const documentosRef = collection(db, `onboarding/${user.dni}/documentos`);
                // Elimina documentos existentes
                const existentesSnap = await getDocs(documentosRef);
                const eliminaciones = [];
                existentesSnap.forEach(documento => {
                    eliminaciones.push(deleteDoc(documento.ref));
                });
                await Promise.all(eliminaciones);

                // Asigna los nuevos documentos
                const guardados = [];
                for (const documento of documentosSeleccionados) {
                    const docRef = doc(db, `onboarding/${user.dni}/documentos`, documento.id);
                    guardados.push(setDoc(docRef, {
                        id: documento.id,
                        title: documento.title,
                        obligatorio: documento.obligatorio,
                        activo: true,
                        completed: false,
                        type: documento.type || "upload",
                        asignadoPor: usuario.email || usuario.nombre,
                        asignadoEl: serverTimestamp()
                    }));
                }
                await Promise.all(guardados);
                totalAsignados++;
            }

            // AUDITORÍA
            try {
                await logBusinessAction("CONFIGURACION_DOCUMENTOS_ONBOARDING", "GestionOnboarding", {
                    cambiosRealizados: {
                        asignacionMasiva: {
                            anterior: null,
                            nuevo: `Asignado a ${totalAsignados} usuarios`
                        }
                    },
                    documentosSeleccionados: documentosSeleccionados.map(d => d.title)
                }, usuario);
            } catch (e) {
                console.warn("No se pudo registrar la auditoría", e);
            }

            Swal.fire({
                title: "¡Asignación masiva exitosa!",
                text: `Se asignaron documentos a ${totalAsignados} usuarios de la empresa.`,
                icon: "success"
            });
        } catch (error) {
            console.error("Error en asignación masiva:", error);
            Swal.fire({
                title: "Error",
                text: "Hubo un problema al asignar los documentos masivamente.",
                icon: "error"
            });
        } finally {
            setGuardando(false);
        }
    };

    // Filtrar documentos según el término de búsqueda
    const documentosFiltrados = documentosUsuario.filter(
        doc => doc.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Función para seleccionar todos los documentos
    const seleccionarTodos = () => {
        setDocumentosUsuario(prevDocs =>
            prevDocs.map(doc => ({ ...doc, selected: true }))
        );
    };

    // Función para deseleccionar todos los documentos
    const deseleccionarTodos = () => {
        setDocumentosUsuario(prevDocs =>
            prevDocs.map(doc => ({ ...doc, selected: false }))
        );
    };

    return (
        <div
            className="min-vh-100"
            style={{
                background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
                minHeight: "100vh",
                width: "100vw",
                maxWidth: "100%",
                padding: "20px 0",
            }}
        >
            <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
                {/* Marco principal */}
                <Card
                    className="shadow-lg border-0"
                    style={{
                        borderRadius: "15px",
                        overflow: "hidden",
                        minHeight: "90vh",
                    }}
                >
                    {/* Header Premium */}
                    <Card.Header
                        className="text-white py-4 position-relative"
                        style={{
                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            borderBottom: "3px solid #f39c12",
                        }}
                    >
                        <div style={{position:"relative",display:"flex",alignItems:"center",justifyContent:"center"}} className="w-100">
                            <div className="text-center">
                                <h2 className="fw-bold mb-1">
                                    <FaCog className="me-3" />
                                    Configuración de Documentos
                                </h2>
                                <p className="mb-0 opacity-75">
                                    Asignación y configuración de documentos para onboarding
                                </p>
                            </div>
                            <div style={{position:"absolute",right:"20px"}}>
                                <BotonManualHeader usuario={usuario} />
                            </div>
                        </div>
                    </Card.Header>

                    <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
                        {/* Mensaje de lista maestra no configurada */}
                        {listaMaestraNoConfigurada && (
                            <Alert variant="warning" className="border-0 shadow-sm mb-4" style={{ borderRadius: "10px" }}>
                                <div className="d-flex align-items-center">
                                    <FaExclamationTriangle className="me-3 fs-4" />
                                    <div>
                                        <h6 className="fw-bold mb-1">Lista maestra no configurada</h6>
                                        <p className="mb-2">
                                            Antes de asignar documentos a usuarios, debes configurar la lista maestra de documentos.
                                        </p>
                                        <Button as={Link} to="/lista-maestra-documentos" variant="primary" size="sm" style={{ borderRadius: "8px" }}>
                                            Ir a configurar lista maestra
                                        </Button>
                                    </div>
                                </div>
                            </Alert>
                        )}

                        {/* Búsqueda por DNI */}
                        <Card className="shadow-sm border-0 mb-4">
                            <Card.Body className="p-4">
                                <Form onSubmit={(e) => { e.preventDefault(); buscarUsuario(); }}>
                                    <Row className="align-items-end">
                                        <Col md={6} lg={6}>
                                            <Form.Group className="mb-3 mb-md-0">
                                                <Form.Label className="fw-semibold text-dark">
                                                    <FaSearch className="me-2" />
                                                    DNI del Usuario
                                                </Form.Label>
                                                <InputGroup>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Ingrese el DNI del usuario"
                                                        value={dniUsuario}
                                                        onChange={(e) => setDniUsuario(e.target.value)}
                                                        className="form-control-lg"
                                                        disabled={listaMaestraNoConfigurada}
                                                        style={{ borderRadius: "10px 0 0 10px" }}
                                                    />
                                                    <Button
                                                        variant="primary"
                                                        onClick={buscarUsuario}
                                                        disabled={loading || listaMaestraNoConfigurada}
                                                        className="d-flex align-items-center"
                                                        style={{
                                                            borderRadius: "0 10px 10px 0",
                                                            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                                            border: "none",
                                                        }}
                                                    >
                                                        {loading ? <Spinner animation="border" size="sm" /> : <FaSearch />}
                                                        <span className="ms-2">Buscar</span>
                                                    </Button>
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Form>
                            </Card.Body>
                        </Card>

                        {/* Error Alert */}
                        {error && (
                            <Alert variant="warning" className="border-0 shadow-sm mb-4" style={{ borderRadius: "10px" }}>
                                <div className="d-flex align-items-center">
                                    <FaExclamationTriangle className="me-3 fs-4" />
                                    <div>
                                        <h6 className="fw-bold mb-1">Error en la consulta</h6>
                                        <p className="mb-0">{error}</p>
                                    </div>
                                </div>
                            </Alert>
                        )}

                        {/* Información del usuario */}
                        {usuarioInfo && (
                            <Card className="mb-4 border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center">
                                        <div 
                                            className="rounded-3 p-3 me-3"
                                            style={{
                                                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                                color: "white"
                                            }}
                                        >
                                            <FaUserTie size={24} />
                                        </div>
                                        <div>
                                            <h5 className="mb-1 fw-bold">
                                                {usuarioInfo.name} {usuarioInfo.apellidos}
                                            </h5>
                                            <p className="text-muted mb-0">
                                                <strong>DNI:</strong> {usuarioInfo.dni} | 
                                                <strong className="ms-2">Email:</strong> {usuarioInfo.email}
                                            </p>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        )}

                        {/* Lista de documentos */}
                        {busquedaRealizada && (
                            <>
                                {/* Header de la tabla con controles */}
                                <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                                    <Card.Header className="bg-primary text-white py-3">
                                        <div className="d-flex align-items-center">
                                            <FaFileAlt className="me-2" />
                                            <div className="flex-grow-1">
                                                <h5 className="mb-0 fw-semibold">Documentos Disponibles</h5>
                                                <small className="opacity-75">
                                                    Configura qué documentos son requeridos para este usuario
                                                </small>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <Badge bg="light" text="primary" style={{ fontSize: "0.9rem" }}>
                                                    {documentosUsuario.filter(doc => doc.selected).length} seleccionados
                                                </Badge>
                                            </div>
                                        </div>
                                    </Card.Header>

                                    {/* Controles de la tabla */}
                                    <Card.Body className="p-4 bg-light">
                                        <Row className="align-items-center">
                                            <Col md={6} lg={6}>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Buscar documento..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    style={{ borderRadius: "10px" }}
                                                />
                                            </Col>
                                            <Col md={6} lg={6}>
                                                <div className="d-flex gap-2 justify-content-end">
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={seleccionarTodos}
                                                        style={{ borderRadius: "8px" }}
                                                    >
                                                        Seleccionar Todos
                                                    </Button>
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        onClick={deseleccionarTodos}
                                                        style={{ borderRadius: "8px" }}
                                                    >
                                                        Deseleccionar
                                                    </Button>
                                                    <Button
                                                        variant="success"
                                                        size="sm"
                                                        onClick={asignarDocumentosATodos}
                                                        disabled={guardando || documentosUsuario.filter(doc => doc.selected).length === 0}
                                                        className="d-flex align-items-center"
                                                        title="Asignar a todos los usuarios de la empresa"
                                                        style={{ borderRadius: "8px" }}
                                                    >
                                                        <FaUsers className="me-1" />
                                                        Asignar a todos
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Card.Body>

                                    {/* Tabla de documentos */}
                                    <Card.Body className="p-0">
                                        <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                                            <Table hover className="mb-0" style={{ fontSize: "0.9rem" }}>
                                                <thead 
                                                    style={{
                                                        backgroundColor: "#f8f9fa",
                                                        position: "sticky",
                                                        top: 0,
                                                        zIndex: 10,
                                                    }}
                                                >
                                                    <tr>
                                                        <th className="border-0 py-3 px-3 text-secondary fw-semibold" style={{ width: '50px' }}>#</th>
                                                        <th className="border-0 py-3 px-3 text-secondary fw-semibold">Documento</th>
                                                        <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center" style={{ width: '120px' }}>Obligatorio</th>
                                                        <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center" style={{ width: '100px' }}>Requerido</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {documentosFiltrados.map((doc, index) => (
                                                        <tr key={doc.id}>
                                                            <td className="py-3 px-3 text-muted">{index + 1}</td>
                                                            <td className="py-3 px-3">
                                                                <div className="d-flex align-items-center">
                                                                    <span className="fw-medium">{doc.title}</span>
                                                                    {doc.obligatorio && doc.selected && (
                                                                        <Badge bg="danger" className="ms-2" pill>Obligatorio</Badge>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="text-center py-3 px-3">
                                                                <Form.Check
                                                                    type="switch"
                                                                    id={`obligatorio-${doc.id}`}
                                                                    checked={doc.obligatorio}
                                                                    onChange={(e) => handleObligatorioChange(doc.id, e.target.checked)}
                                                                    disabled={!doc.selected}
                                                                />
                                                            </td>
                                                            <td className="text-center py-3 px-3">
                                                                <Form.Check
                                                                    type="switch"
                                                                    id={`selected-${doc.id}`}
                                                                    checked={doc.selected}
                                                                    onChange={(e) => handleDocumentoChange(doc.id, e.target.checked)}
                                                                />
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {documentosFiltrados.length === 0 && (
                                                        <tr>
                                                            <td colSpan="4" className="text-center py-5 text-muted">
                                                                <FaFileAlt size={32} className="mb-2" />
                                                                <p>No se encontraron documentos que coincidan con la búsqueda</p>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Card.Body>

                                    {/* Footer con botón guardar */}
                                    <Card.Footer className="border-0 bg-light p-4">
                                        <div className="d-flex justify-content-end">
                                            <Button
                                                variant="primary"
                                                onClick={guardarConfiguracion}
                                                disabled={guardando}
                                                className="d-flex align-items-center px-4"
                                                style={{
                                                    borderRadius: "10px",
                                                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                                    border: "none",
                                                }}
                                                size="lg"
                                            >
                                                {guardando ? (
                                                    <>
                                                        <Spinner animation="border" size="sm" className="me-2" />
                                                        Guardando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaSave className="me-2" />
                                                        Guardar configuración
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </Card.Footer>
                                </Card>
                            </>
                        )}

                        {/* Estado inicial - sin búsqueda realizada */}
                        {!busquedaRealizada && !loading && !listaMaestraNoConfigurada && (
                            <Card className="shadow-sm border-0" style={{ borderRadius: "15px" }}>
                                <Card.Body className="py-5">
                                    <div className="text-center">
                                        <div
                                            className="bg-light rounded-circle mx-auto mb-3"
                                            style={{
                                                width: "80px",
                                                height: "80px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <FaSearch size={32} className="text-muted" />
                                        </div>
                                        <h5 className="text-muted mb-2">
                                            Ingrese un DNI para configurar los documentos requeridos
                                        </h5>
                                        <p className="text-muted">
                                            La configuración de documentos se guardará en el perfil del usuario
                                        </p>
                                    </div>
                                </Card.Body>
                            </Card>
                        )}

                        {/* Información sobre la lista maestra */}
                        <Alert variant="info" className="border-0 shadow-sm mt-4" style={{ borderRadius: "10px" }}>
                            <div className="d-flex align-items-center">
                                <FaInfoCircle className="me-3" size={20} />
                                <div>
                                    <p className="mb-0">
                                        Los documentos mostrados provienen de la lista maestra configurada para tu empresa.
                                        Para gestionar qué documentos aparecen en esta lista, visita la sección de 
                                        <Link to="/listaMaestra" className="ms-1 fw-bold text-decoration-none">Lista Maestra de Documentos</Link>.
                                    </p>
                                </div>
                            </div>
                        </Alert>
                    </Card.Body>
                </Card>
            </Container>
        </div>
    );
}

export default ConfiguracionDocumentos;